import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '../locales/en/common.json';
import enHome from '../locales/en/home.json';
import enLegal from '../locales/en/legal.json';
import enDemo from '../locales/en/demo.json';
import deCommon from '../locales/de/common.json';
import deHome from '../locales/de/home.json';
import deLegal from '../locales/de/legal.json';
import deDemo from '../locales/de/demo.json';
import frCommon from '../locales/fr/common.json';
import frHome from '../locales/fr/home.json';
import frLegal from '../locales/fr/legal.json';
import frDemo from '../locales/fr/demo.json';
import esCommon from '../locales/es/common.json';
import esHome from '../locales/es/home.json';
import esLegal from '../locales/es/legal.json';
import esDemo from '../locales/es/demo.json';
import ptCommon from '../locales/pt/common.json';
import ptHome from '../locales/pt/home.json';
import ptLegal from '../locales/pt/legal.json';
import ptDemo from '../locales/pt/demo.json';
import hiCommon from '../locales/hi/common.json';
import hiHome from '../locales/hi/home.json';
import hiLegal from '../locales/hi/legal.json';
import hiDemo from '../locales/hi/demo.json';
import zhCommon from '../locales/zh/common.json';
import zhHome from '../locales/zh/home.json';
import zhLegal from '../locales/zh/legal.json';
import zhDemo from '../locales/zh/demo.json';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'es', 'pt', 'hi', 'zh'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'de';

// Language names in their native scripts
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
  hi: 'हिन्दी',
  zh: '简体中文',
};

// Flag emojis for each language
export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: '🇺🇸',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  pt: '🇵🇹',
  hi: '🇮🇳',
  zh: '🇨🇳',
};

// Translation resources
export const resources = {
  en: {
    common: enCommon,
    home: enHome,
    legal: enLegal,
    demo: enDemo,
  },
  de: {
    common: deCommon,
    home: deHome,
    legal: deLegal,
    demo: deDemo,
  },
  fr: {
    common: frCommon,
    home: frHome,
    legal: frLegal,
    demo: frDemo,
  },
  es: {
    common: esCommon,
    home: esHome,
    legal: esLegal,
    demo: esDemo,
  },
  pt: {
    common: ptCommon,
    home: ptHome,
    legal: ptLegal,
    demo: ptDemo,
  },
  hi: {
    common: hiCommon,
    home: hiHome,
    legal: hiLegal,
    demo: hiDemo,
  },
  zh: {
    common: zhCommon,
    home: zhHome,
    legal: zhLegal,
    demo: zhDemo,
  },
} as const;

// Initialize i18next
i18n
  // Detect language from URL, localStorage, or browser
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language (will be overridden by detector)
    lng: undefined, // Let the detector determine the language
    // Fallback language
    fallbackLng: DEFAULT_LANGUAGE,
    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES,
    // Translation resources
    resources,
    // Language detection options
    detection: {
      // Order of detection - localStorage first (set by LanguageSwitcher), then browser
      // We DON'T use 'path' here because React Router handles URL-based language detection
      order: ['localStorage', 'navigator'],
      // Cache user language in localStorage
      caches: ['localStorage'],
      // Cookie name for language persistence
      cookieMinutes: 60 * 24 * 365, // 1 year
    },
    // Allow empty strings (useful for missing translations)
    returnEmptyString: false,
    // Return key if translation is missing
    returnObjects: true,
    // Debug mode (set to false in production)
    debug: import.meta.env.DEV,
    // Default namespace
    defaultNS: 'common',
    // Namespaces to load
    ns: ['common', 'home', 'legal', 'demo'],
    // React settings
    react: {
      // Use Suspense to wait for translations to load
      useSuspense: false,
    },
    // Interpolation settings
    interpolation: {
      // Escape HTML entities
      escapeValue: false,
    },
  });

export default i18n;
